<template>
  <div class="tip-1-page" :class="{ 'blog-page': blogOnly }">
    <!-- Header Navigation -->
    <HeadNav />
    <!-- Breadcrumb -->
    <Breadcrumb :breadcrumbs="breadcrumbs" />
    
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">{{ pageTitle }}</h1>
        <p class="hero-subtitle"></p>
      </div>
    </section>
    
    <!-- Main Content -->
    <main class="main-content">
      <div class="container">
        <!-- Tips List -->
        <div class="tips-grid">
          <div 
            v-for="(tip, index) in displayedTips"
            :key="index" 
            class="tip-card"
            @click="goToTip(tip.url)"
          >
            <div class="tip-image">
              <img :src="tip.poster" :alt="tip.title" />
            </div>
            <div class="tip-content">
              <h2 class="tip-title">{{ tip.title }}</h2>
              <p class="tip-desc">{{ tip.desc }}</p>
              <div class="tip-meta">
                <span v-if="tip.date" class="tip-date">{{ tip.date }}</span>
                <span class="tip-read-more"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    
  </div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import Footer from '@/components/Footer.vue'
import tipListData from '@/documents/tipList.js'

export default {
  name: 'DownloaderTips',
  components: {
    HeadNav,
    Breadcrumb,
    Footer
  },
  props: {
    pageTitle: {
      type: String,
      default: 'Tips'
    },
    blogOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tipList: tipListData.tipList.en
    }
  },
  computed: {
    displayedTips() {
      const tips = this.blogOnly
        ? this.tipList.filter((tip) => tip.type === 'blog')
        : this.tipList;

      return tips.slice().reverse();
    },
    breadcrumbs() {
      return this.$route.matched.reduce((acc, route) => {
        const crumbs = route.meta.breadcrumb || [];
        return acc.concat(crumbs);
      }, []);
    }
  },
  methods: {
    goToTip(url) {
      const target = new URL(url, window.location.origin);
      this.$router.push(`${target.pathname}${target.search}${target.hash}`);
    }
  }
}
</script>

<style scoped>
.tip-1-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.hero-section {
  padding: 10px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
  animation: float 20s ease-in-out infinite;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.hero-subtitle {
  font-size: 1.2rem;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

.main-content {
  padding: 0px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  padding-bottom: 100px;
}

.tips-grid {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 40px;
}

.tip-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
  display: flex;
}

.tip-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.tip-image {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.tip-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.tip-card:hover .tip-image img {
  transform: scale(1.08);
}

.tip-content {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tip-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  letter-spacing: -0.02em;
}

.tip-desc {
  color: #6b7280;
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tip-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.tip-date {
  color: #9ca3af;
  font-size: 0.85rem;
  font-weight: 500;
}

.tip-read-more {
  color: #00D4AA;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  position: relative;
}

.tip-read-more::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: #00D4AA;
  transition: width 0.3s ease;
}

.tip-card:hover .tip-read-more {
  color: #00B894;
}

.tip-card:hover .tip-read-more::after {
  width: 100%;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .hero-section {
    padding: 60px 0;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 30px;
  }
  
  .tip-card {
    margin: 0 10px;
  }
  
  .tip-image {
    height: 200px;
  }
  
  .tip-content {
    padding: 24px;
    gap: 14px;
  }
  
  .tip-title {
    font-size: 1.2rem;
  }
  
  .tip-desc {
    font-size: 0.9rem;
  }
  
  .tip-meta {
    padding-top: 16px;
  }
}

/* Blog listing layout */
.blog-page {
  background: #f4f6f9;
}

.blog-page .hero-section {
  padding: 38px 0 8px;
  background: transparent;
}

.blog-page .hero-title {
  margin-bottom: 0;
  color: #172033;
  font-size: 2.5rem;
  letter-spacing: 0;
  text-shadow: none;
}

.blog-page .container {
  max-width: 1160px;
  padding-bottom: 72px;
}

.blog-page .tips-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  margin-top: 28px;
}

.blog-page .tip-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(23, 32, 51, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.blog-page .tip-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 36px rgba(23, 32, 51, 0.13);
}

.blog-page .tip-image {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  background: #eef1f5;
}

.blog-page .tip-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-page .tip-content {
  flex: 1;
  gap: 12px;
  padding: 24px 26px 26px;
}

.blog-page .tip-title {
  color: #172033;
  font-size: 1.35rem !important;
  line-height: 1.35;
  letter-spacing: 0;
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.blog-page .tip-desc {
  color: #667085;
  font-size: 0.95rem !important;
  line-height: 1.65;
}

.blog-page .tip-meta {
  padding-top: 18px;
  border-top-color: #edf0f4;
}

.blog-page .tip-date {
  color: #667085;
  font-size: 0.83rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.blog-page .tip-read-more {
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-top: 2px solid #00b894;
  border-right: 2px solid #00b894;
  transform: rotate(45deg);
  transition: transform 0.25s ease, border-color 0.25s ease;
}

.blog-page .tip-read-more::after {
  display: none;
}

.blog-page .tip-card:hover .tip-read-more {
  border-color: #008f73;
  transform: translateX(3px) rotate(45deg);
}

@media (max-width: 900px) {
  .blog-page .hero-section {
    padding: 30px 0 4px;
  }

  .blog-page .hero-title {
    font-size: 2.25rem;
  }

  .blog-page .tips-grid {
    grid-template-columns: 1fr;
    gap: 22px;
    margin-top: 24px;
  }
}

@media (max-width: 560px) {
  .blog-page .hero-section {
    padding: 22px 0 0;
  }

  .blog-page .hero-title {
    font-size: 2rem;
  }

  .blog-page .container {
    padding: 0 16px 56px;
  }

  .blog-page .tips-grid {
    gap: 18px;
    margin-top: 20px;
  }

  .blog-page .tip-card {
    margin: 0;
    border-radius: 8px;
  }

  .blog-page .tip-image {
    height: auto;
  }

  .blog-page .tip-content {
    gap: 10px;
    padding: 20px;
  }

  .blog-page .tip-title {
    font-size: 1.2rem;
    line-height: 1.35;
  }

  .blog-page .tip-desc {
    font-size: 0.92rem;
    line-height: 1.55;
  }

  .blog-page .tip-meta {
    padding-top: 16px;
  }
}
</style>



